import { NetworkStatus } from '@apollo/client';
import type {
  OperationVariables,
  DocumentNode,
  TypedDocumentNode,
  MaybeMasked,
} from '@apollo/client';
import {
  skipToken,
  useApolloClient,
  useQuery,
  useReactiveVar,
  useSubscription,
} from '@apollo/client/react';
import { useEffect, useEffectEvent } from 'react';
import { errorCode } from '@guuru/graphql-common';
import type { Client } from '@guuru/graphql-common';

type ErrorHandler = (_: Error) => void;

interface LiveQueryHookOptions<TData, TVariables extends OperationVariables> {
  queryOptions?: Partial<Omit<useQuery.Options<TData, TVariables>, 'skip'>>;
  subscriptionOptions?:
    | Partial<Omit<useSubscription.Options<TData, TVariables>, 'skip'>>
    | typeof skipToken;
  variables?: Partial<TVariables>;
  onError?: ErrorHandler;
  onData?: (_: NonNullable<MaybeMasked<TData>>) => void;
}

const useLiveQuery = function <
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
>(
  queryDocument: DocumentNode | TypedDocumentNode<TData, TVariables>,
  subscriptionDocument: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options: LiveQueryHookOptions<TData, TVariables> | typeof skipToken = {},
) {
  const skip = options === skipToken;
  const {
    onError,
    onData,
    variables,
    queryOptions = {},
    subscriptionOptions = {},
  }: LiveQueryHookOptions<TData, TVariables> = skip ? {} : options;
  const client = useApolloClient() as Client<unknown>;
  const connectedAt = useReactiveVar(client.connectedAt);
  const skipSubscription = skip || subscriptionOptions === skipToken;

  const query = useQuery(
    queryDocument,
    skip || !(connectedAt || skipSubscription)
      ? skipToken
      : {
          ...queryOptions,
          variables: {
            ...variables,
            ...queryOptions.variables,
          },
        },
  );

  useSubscription(
    subscriptionDocument,
    skipSubscription
      ? skipToken
      : {
          ...subscriptionOptions,
          variables: {
            ...variables,
            ...subscriptionOptions.variables,
          },
        },
  );

  const { data, refetch, networkStatus } = query;

  const refetchOnConnection = useEffectEvent(() => {
    if (networkStatus !== NetworkStatus.ready) return;
    refetch().catch((err: Error) => {
      if (err.name === 'AbortError') return;
      if (onError) {
        onError(err);
        return;
      }
      if (!['FORBIDDEN', 'NOT_FOUND'].includes(errorCode(err))) {
        throw err;
      }
    });
  });

  const notifyData = useEffectEvent(() => {
    if (data) onData?.(data);
  });

  useEffect(() => {
    notifyData();
  }, [data]);

  useEffect(() => {
    if (skipSubscription || !connectedAt) return;
    refetchOnConnection();
  }, [connectedAt, skipSubscription]);

  return query;
};

export default useLiveQuery;
